// Components
import ToastProvider from 'common/ToastProvider'
import MediaUpload from 'components/organisms/MediaUpload/MediaUpload'
import TextInput from 'components/molecules/TextInput/TextInput'
import Select from 'components/molecules/Select/Select'
import { CompanySearchBar } from 'components/CompanySearchBar'
import { Controller } from 'react-hook-form'
import { Option } from 'common/types'
import { ButtonBase, Typography } from '@mui/material'
import { DateInput } from 'components/mui'
import { LoadingButton } from '@mui/lab'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'

// Styles
import './ProductRequestForm.scss'

// Logic
import useProductRequestFormLogic from './ProductRequestForm.logic'
import { ProductRequestFormProps } from '../ManualAssetEntry.type'

const ProductRequestForm = (props: ProductRequestFormProps) => {
  const {
    errors,
    control,
    hasDeviceCount,
    isCreatingRequest,
    unitOfMeasureOptions,
    showAttachmentsError,
    isUploadingAttachments,
    navigate,
    handleOnSubmit,
    setAttachments,
  } = useProductRequestFormLogic(props)

  return (
    <ToastProvider>
      <form onSubmit={handleOnSubmit} className="product-request-form">
        <div className="product-request-header">
          <ButtonBase disableRipple onClick={() => navigate(-1)}>
            <KeyboardBackspaceIcon color="primary" className="back-icon" />
          </ButtonBase>
          <Typography variant="h2">Product Request</Typography>
          <div />
        </div>
        <div className="product-request-form-body">
          <Controller
            name="deviceId"
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextInput
                sx={{ m: 0 }}
                label="Device ID"
                variant="outlined"
                fullWidth
                error={!!errors.deviceId}
                helperText={errors.deviceId?.message}
                handleChangeFormData={(e) => onChange(e.target.value)}
                value={value}
                {...rest}
              />
            )}
          />
          <Controller
            name="catalogNumber"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Version/Catalog number is required',
              },
            }}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextInput
                sx={{ m: 0 }}
                label="Version/Catalog Number*"
                variant="outlined"
                fullWidth
                value={value}
                error={!!errors.catalogNumber}
                helperText={errors.catalogNumber?.message}
                handleChangeFormData={(e) => onChange(e.target.value)}
                {...rest}
              />
            )}
          />
          <Controller
            name="serialNumber"
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextInput
                sx={{ m: 0 }}
                label="Lot/Serial"
                variant="outlined"
                fullWidth
                value={value}
                error={!!errors.serialNumber}
                helperText={errors.serialNumber?.message}
                handleChangeFormData={(e) => onChange(e.target.value)}
                {...rest}
              />
            )}
          />
          <Controller
            name="expirationDate"
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <DateInput
                placeholder="MM/DD/YYYY"
                format="MM/DD/YYYY"
                className="date-input"
                readOnly
                openTo="month"
                handleChangeFormData={onChange}
                label="Expiration Date"
                value={value}
                error={!!errors.expirationDate}
                variant="outlined"
                helperText={errors.expirationDate?.message}
                fullWidth
                shrink={!!value}
                sx={{
                  backgroundColor: 'white',
                }}
                {...rest}
              />
            )}
          />
          <Controller
            name="company"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Company is required',
              },
            }}
            render={({ field: { onChange, value } }) => (
              <CompanySearchBar
                handleChangeFormData={(e: Option) => onChange(e)}
                variant="outlined"
                required={false}
                label="Search Company*"
                placeholder="Search companies"
                error={!!errors.company}
                helperText={errors.company?.message}
                propValue={value}
              />
            )}
          />
          <Controller
            name="deviceDescription"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Description is required',
              },
            }}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextInput
                sx={{ m: 0 }}
                label="Product Description*"
                variant="outlined"
                fullWidth
                value={value}
                error={!!errors.deviceDescription}
                helperText={errors.deviceDescription?.message}
                handleChangeFormData={(e) => onChange(e.target.value)}
                {...rest}
              />
            )}
          />
          <Controller
            name="unitOfMeasure"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Unit of measure is required',
              },
            }}
            render={({ field: { onChange, value, ...rest } }) => (
              <Select
                selectedValue={value}
                label="Unit of Measure*"
                variant="outlined"
                placeholder={
                  unitOfMeasureOptions.find((option) => option.id === value)
                    ?.name
                }
                fullWidth
                error={!!errors.unitOfMeasure}
                helperText={errors.unitOfMeasure?.message}
                onSelect={(e) => onChange(e.id)}
                options={unitOfMeasureOptions}
                shrink={!!value}
                {...rest}
              />
            )}
          />

          {hasDeviceCount ? (
            <Controller
              name="deviceCount"
              control={control}
              rules={{
                required: {
                  value: hasDeviceCount,
                  message: 'Device count is required',
                },
                pattern: {
                  value: /^[0-9]*$/,
                  message: 'Please enter numbers only',
                },
              }}
              render={({ field: { onChange, value, ...rest } }) => (
                <TextInput
                  label="Device Count*"
                  variant="outlined"
                  fullWidth
                  type="text"
                  inputMode="numeric"
                  error={!!errors.deviceCount}
                  helperText={errors.deviceCount?.message}
                  handleChangeFormData={(e) => {
                    if (/\D/.test(e.target.value)) return
                    onChange(e.target.value)
                  }}
                  shrink={!!value}
                  value={value}
                  {...rest}
                />
              )}
            />
          ) : null}

          <div className="product-request-form-images">
            <Typography sx={{ marginBottom: '5px' }} variant="h6">
              Upload Images
            </Typography>
            <MediaUpload
              disableActions={isCreatingRequest || isUploadingAttachments}
              isLocal
              hasActions
              onLocalMediaFilesChange={(files) => setAttachments(files)}
            />
            {showAttachmentsError && (
              <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                Uploading at least one image is required
              </Typography>
            )}
          </div>
        </div>
        <LoadingButton
          loading={isCreatingRequest || isUploadingAttachments}
          variant="contained"
          color="primary"
          type="submit"
        >
          Submit
        </LoadingButton>
      </form>
    </ToastProvider>
  )
}

export default ProductRequestForm
